import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { SalesActions } from "@/components/crm/SalesActions";
import { OpportunityDialog } from "@/components/crm/OpportunityDialog";
import { PipelineBoard } from "@/components/crm/PipelineBoard";
import { RecordDrawer } from "@/components/crm/RecordDrawer";
import { SalesShell } from "@/components/crm/SalesShell";
import { EmptyState, Stat } from "@/components/crm/parts";
import { suggestionsFor } from "@/lib/crm/suggestions";
import { pipelineVisual } from "@/lib/crm/visual-sources";
import { useSalesOwner } from "@/lib/crm/owner-filter";
import { pipelineSheets } from "@/lib/exports/crm";
import { useOrgMembers } from "@/lib/crm/useOrgMembers";
import {
  useAccounts,
  useContracts,
  useOpportunities,
  useSaveRecord,
  useStages,
} from "@/lib/crm/useCrm";
import { gbp, isOpen, money, weightedValue, type Opportunity } from "@/lib/crm/types";

export const Route = createFileRoute("/_authenticated/sales/pipeline")({
  head: () => ({
    meta: [
      { title: "Pipeline — MyPriorities" },
      {
        name: "description",
        content:
          "A configurable sales pipeline with gross and stage-weighted values, so forecasts stay honest.",
      },
      { property: "og:title", content: "Pipeline — MyPriorities" },
      {
        property: "og:description",
        content: "Move opportunities through your own stages and see weighted value update live.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: PipelinePage,
});

function PipelinePage() {
  return (
    <SalesShell
      title="Pipeline"
      subtitle="Your stages, your weightings — gross value is never overwritten."
    >
      {({ orgId, userId }) => <Pipeline orgId={orgId} userId={userId} />}
    </SalesShell>
  );
}

function Pipeline({ orgId, userId }: { orgId: string; userId: string }) {
  const stages = useStages(orgId);
  const opportunities = useOpportunities(orgId);
  const accounts = useAccounts(orgId);
  const contracts = useContracts(orgId);
  const { ownerLabel, ownerOptions } = useOrgMembers(orgId);
  const save = useSaveRecord("crm_opportunities");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Opportunity | null>(null);
  const [detail, setDetail] = useState<Opportunity | null>(null);

  const { scope } = useSalesOwner();
  const stageRows = stages.data ?? [];
  const oppRows = scope(opportunities.data ?? []);
  const accountRows = accounts.data ?? [];
  const open = oppRows.filter((o) => isOpen(o, stageRows));

  const move = async (opp: Opportunity, stageId: string) => {
    try {
      await save.mutateAsync({ id: opp.id, values: { stage_id: stageId } });
      toast.success("Stage updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not move that opportunity");
    }
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="grid flex-1 gap-3 sm:grid-cols-3">
          <Stat label="Open opportunities" value={String(open.length)} />
          <Stat
            label="Gross pipeline"
            value={gbp(open.reduce((s, o) => s + money(o.expected_value), 0))}
          />
          <Stat
            label="Weighted pipeline"
            value={gbp(open.reduce((s, o) => s + weightedValue(o, stageRows), 0))}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <SalesActions
            exports={[
              {
                label: "Pipeline",
                baseName: "pipeline",
                sheets: () =>
                  pipelineSheets({
                    opportunities: oppRows,
                    accounts: accountRows,
                    stages: stageRows,
                    ownerLabel,
                  }),
              },
            ]}
            visual={() => pipelineVisual(oppRows, stageRows)}
            sourceNote="From your sales pipeline"
          />
          <Button
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="size-4" /> New opportunity
          </Button>
        </div>
      </div>

      {stageRows.length === 0 ? (
        <EmptyState
          title="No stages yet"
          body="Add your pipeline stages in Sales settings, then opportunities can move through them."
        />
      ) : (
        <PipelineBoard
          stages={stageRows}
          opportunities={oppRows}
          accounts={accountRows}
          onOpen={(opp) => setDetail(opp)}
          onMove={(opp, stageId) => void move(opp, stageId)}
        />
      )}

      <OpportunityDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        orgId={orgId}
        userId={userId}
        opportunity={editing}
        accounts={accountRows}
        contracts={contracts.data ?? []}
        stages={stageRows}
        ownerOptions={ownerOptions}
      />

      <RecordDrawer
        open={Boolean(detail)}
        onOpenChange={(v) => !v && setDetail(null)}
        orgId={orgId}
        userId={userId}
        kind="opportunity"
        recordId={detail?.id ?? null}
        title={detail?.name ?? ""}
        subtitle={
          detail
            ? `${gbp(detail.expected_value)} gross · ${gbp(weightedValue(detail, stageRows))} weighted`
            : undefined
        }
        suggestions={detail ? suggestionsFor(detail, stageRows) : []}
        header={
          detail ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditing(detail);
                setDetail(null);
                setDialogOpen(true);
              }}
            >
              Edit opportunity
            </Button>
          ) : null
        }
      />
    </div>
  );
}
